import { fetchWallboxPrices } from 'src/Libs/e-mobility/emobility-wallbox/api/fetch-wallbox-prices.api'
import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import { EMobilityWallboxPricesModel } from 'src/Libs/e-mobility/emobility-wallbox/models/e-mobility-wallbox-models'
import { usePortalQuery } from 'src/Libs/api'

interface UseFetchWallboxPricesHook {
  chargePrices: EMobilityWallboxPricesModel[] | undefined
  dischargePrices: EMobilityWallboxPricesModel[] | undefined
  isChargePricesLoading: boolean
  isChargePricesError: boolean
  isDischargePricesLoading: boolean
  isDischargePricesError: boolean
}

type UseFetchWallboxPricesProps = {
  serialNo: string
  wallboxIndex: number
}

/**
 * Hook for fetching prices per single wallbox.
 * It fetches both charging and discharging prices.
 * @param serialNo installation serial no.
 * @param wallboxIndex as wallbox number endpoint param
 * @return UseFetchWallboxPricesHook interface
 */
export const useFetchWallboxPrices = ({ serialNo, wallboxIndex }: UseFetchWallboxPricesProps): UseFetchWallboxPricesHook => {
  const authFetch = useAuthFetchWithResult()
  const {
    data: chargePrices,
    isLoading: isChargePricesLoading,
    isError: isChargePricesError,
  } = usePortalQuery({
    queryKey: ['wallboxChargePrices', serialNo, wallboxIndex],
    queryFn: () => fetchWallboxPrices(serialNo, wallboxIndex, 'charge', authFetch),
  })

  const {
    data: dischargePrices,
    isLoading: isDischargePricesLoading,
    isError: isDischargePricesError,
  } = usePortalQuery({
    queryKey: ['wallboxDischargePrices', serialNo, wallboxIndex],
    queryFn: () => fetchWallboxPrices(serialNo, wallboxIndex, 'discharge', authFetch),
    enabled: Boolean(chargePrices && Object.keys(chargePrices).length > 0),
  })

  return {
    chargePrices: chargePrices ?? undefined,
    dischargePrices: dischargePrices ?? undefined,
    isChargePricesLoading,
    isChargePricesError,
    isDischargePricesLoading,
    isDischargePricesError,
  }
}
